import { useEffect, useMemo, useRef, useState } from 'react';
import { useCustomerOptions, type CustomerOption } from '../lib/queries';
import { t } from '../i18n';

function score(o: CustomerOption, q: string) {
  const n = o.businessName.toLowerCase();
  if (n.startsWith(q)) return 3;
  if (n.includes(q)) return 2;
  if (o.companyNumber?.includes(q)) return 2;
  return 0;
}

/** Searchable customer selector (keyboard: ↑ ↓ Enter Esc). */
export function CustomerPicker({
  value,
  onChange,
  exclude,
  placeholder = 'חיפוש לקוח לפי שם או ח.פ…',
  onlyTopLevel,
  id,
}: {
  value: number | null;
  onChange: (id: number | null, option?: CustomerOption) => void;
  exclude?: number[];
  placeholder?: string;
  onlyTopLevel?: boolean;
  id?: string;
}) {
  const { data } = useCustomerOptions();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const selected = data?.items.find((o) => o.id === value) ?? null;

  const results = useMemo(() => {
    const all = (data?.items ?? []).filter((o) => !exclude?.includes(o.id) && (!onlyTopLevel || !o.parentId));
    const qq = q.trim().toLowerCase();
    if (!qq) return all.slice(0, 50);
    return all
      .map((o) => ({ o, s: score(o, qq) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s || a.o.businessName.localeCompare(b.o.businessName, 'he'))
      .slice(0, 50)
      .map((x) => x.o);
  }, [data, q, exclude, onlyTopLevel]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (o: CustomerOption) => {
    onChange(o.id, o);
    setQ('');
    setOpen(false);
  };

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <input
        id={id}
        className="input"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        placeholder={selected ? selected.businessName : placeholder}
        value={open ? q : (selected?.businessName ?? '')}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(results.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter' && open) {
            e.preventDefault();
            const o = results[active];
            if (o) pick(o);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open ? (
        <div className="popover" style={{ insetInline: 0, top: 38, maxHeight: 280, overflowY: 'auto' }} role="listbox">
          {results.length === 0 ? (
            <div className="menu-group-label" style={{ padding: 12 }}>לא נמצאו לקוחות</div>
          ) : (
            results.map((o, i) => (
              <button
                type="button"
                key={o.id}
                role="option"
                aria-selected={o.id === value}
                className={`menu-item ${i === active ? 'active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}
              >
                <span className="ellipsis" style={{ flex: 1 }}>{o.businessName}</span>
                {o.companyNumber ? <bdi className="small muted tabular">{o.companyNumber}</bdi> : null}
                {o.status !== 'active' ? <span className="badge">{t.enums.customerStatus[o.status as 'active']}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
